import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Modal } from "@/components/ui/modal";
import {
  RECOVERY_REQUEST_2024_001,
  RECOVERY_APPROVALS_2024_001,
  RECOVERY_APPROVAL_QUORUM_NOTE,
} from "@/data/mock";
import { usePermission } from "@/hooks/use-permission";
import type { CustodianApprovalStatus } from "@/types";

/* RCV-03 — Recovery Approval Ceremony.
   Canonical source: prototype/pages/recovery/approval.html (no current
   Figma frame located). Two deliberate deviations from the canonical
   markup: (1) the canonical "Reject Request" action uses a blocking
   native `confirm()` dialog — replaced here with the app's existing
   Modal component, preserving identical semantics (confirm → navigate
   to /recovery). (2) the canonical success-state "Begin Ceremony →"
   link targets hsm-auth.html (not a required screen) — redirected here
   directly to RCV-04 (/recovery/execution), consistent with the
   blueprint's own canonical RCV-03 → RCV-04 flow ordering.

   Per DD-006, this screen shows its own independent static fixture
   (REC-2024-001) — approving/rejecting here does not mutate RCV-02's
   or RCV-04/05's separately-fixtured request threads. */

const STATUS_TONE: Record<CustodianApprovalStatus, "success" | "info" | "warning" | "neutral"> = {
  "Approved automatically": "success",
  Approved: "success",
  Pending: "warning",
  Abstained: "neutral",
};

export function RecoveryApproval() {
  const navigate = useNavigate();
  const canApprove = usePermission("admin:access");
  const [outcome, setOutcome] = useState<"pending" | "approved">("pending");
  const [rejectOpen, setRejectOpen] = useState(false);

  const req = RECOVERY_REQUEST_2024_001;

  if (outcome === "approved") {
    return (
      <div className="mx-auto max-w-xl rounded-[10px] border border-line bg-surface p-8 text-center">
        <div className="mb-3 text-4xl">✓</div>
        <div className="mb-1 text-base font-bold text-ink">Recovery Process Initiated</div>
        <p className="mb-5 text-sm text-muted">Quorum reached for request {req.id}. The recovery ceremony can now begin.</p>
        <div className="flex flex-wrap justify-center gap-2.5">
          <Link to="/recovery/execution">
            <Button>Begin Ceremony →</Button>
          </Link>
          <Link to="/recovery">
            <Button variant="secondary">← Back to Key Recovery</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Recovery Approval — {req.id}</h1>
          <p className="text-sm text-muted">Submitted by {req.requester} · 10 minutes ago</p>
        </div>
        <Badge variant="warning">Pending Approval</Badge>
      </div>

      <div className="mb-5 grid gap-4 lg:grid-cols-2">
        <div className="rounded-[10px] border border-line bg-surface p-6">
          <div className="mb-4 text-base font-bold text-ink">Request Summary</div>
          <dl className="space-y-2.5 text-sm">
            <Row k="Type" v={req.type} />
            <Row k="Reason" v={req.reason} />
            <Row k="Affected Wallet" v={req.affectedWallet} />
            <Row k="Urgency" v={req.urgency} />
            <Row k="Requester" v={`${req.requester} — ${req.requesterRole}`} />
            <Row k="Submitted" v={req.submitted} />
          </dl>
        </div>

        <div className="rounded-[10px] border border-line bg-surface p-6">
          <div className="mb-4 text-base font-bold text-ink">Custodian Approvals</div>
          <ul className="divide-y divide-line">
            {RECOVERY_APPROVALS_2024_001.map((a) => (
              <li key={a.name} className="flex items-center justify-between gap-3 py-2.5">
                <div className="flex items-center gap-2.5">
                  <span
                    aria-hidden
                    className="grid size-7 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white"
                    style={{ backgroundColor: a.color }}
                  >
                    {a.initials}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-ink">{a.name}{a.isMine && <span className="ml-1.5 text-xs font-bold text-primary">(YOU)</span>}</div>
                    <div className="text-xs text-muted">{a.role}</div>
                  </div>
                </div>
                <Badge variant={STATUS_TONE[a.status]} size="sm">{a.status}</Badge>
              </li>
            ))}
          </ul>
          <div className="mt-4 rounded-md border border-line bg-bg p-3 text-xs text-muted">{RECOVERY_APPROVAL_QUORUM_NOTE}</div>
        </div>
      </div>

      <div className="flex gap-2.5">
        <Button variant="destructive" disabled={!canApprove} onClick={() => canApprove && setRejectOpen(true)}>✗ Reject Request</Button>
        <Button variant="success" disabled={!canApprove} onClick={() => canApprove && setOutcome("approved")}>✓ Approve Request</Button>
      </div>

      <Modal
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject recovery request?"
        description={`This will reject request ${req.id}. The requester will be notified. This action cannot be undone.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={!canApprove} onClick={() => canApprove && navigate("/recovery")}>Reject Request</Button>
          </>
        }
      >
        <p className="text-sm text-muted">Rejecting this request returns it to the requester for revision.</p>
      </Modal>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-muted">{k}</dt>
      <dd className="text-right font-medium text-ink">{v}</dd>
    </div>
  );
}
